/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import '../styles/components.css';

interface Notification {
  id: string;
  type: 'fund_request' | 'project_review' | 'changes_requested' | 'kpi_report' | 'compliance';
  title: string;
  message: string;
  link: string;
  read: boolean;
  created_at: string;
}

interface NotificationBellProps {
  userRole: 'client' | 'ngo';
  companyId?: string;
  ngoId?: string;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ userRole, companyId, ngoId }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if ((userRole === 'client' && !companyId) || (userRole === 'ngo' && !ngoId)) {
      setLoading(false);
      return;
    }

    fetchNotifications();

    // Set up real-time subscription
    const channel = supabase
      .channel('notifications')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'fund_requests' },
        () => {
          fetchNotifications();
        }
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'project_ngos' },
        () => {
          fetchNotifications();
        }
      )
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'projects' },
        () => {
          // Watch for changes to projects table (e.g., changes_requested field)
          fetchNotifications();
        }
      )
      .subscribe();

    const kpiChannel = supabase
      .channel('kpi_reports_changes')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'kpi_reports' },
        () => fetchNotifications()
      )
      .subscribe();

    const complianceChannel = supabase
      .channel('compliance_changes')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'document_uploads' },
        () => fetchNotifications()
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'document_requests' },
        () => fetchNotifications()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      supabase.removeChannel(kpiChannel);
      supabase.removeChannel(complianceChannel);
    };
  }, [userRole, companyId, ngoId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const getReadNotifications = (): Set<string> => {
    try {
      const read = localStorage.getItem(`notifications_read_${userRole}_${companyId || ngoId}`);
      return read ? new Set(JSON.parse(read)) : new Set();
    } catch {
      return new Set();
    }
  };

  const markNotificationAsRead = (notificationId: string) => {
    try {
      const read = getReadNotifications();
      read.add(notificationId);
      localStorage.setItem(`notifications_read_${userRole}_${companyId || ngoId}`, JSON.stringify(Array.from(read)));
      // Update local state
      setNotifications(prev => prev.map(n =>
        n.id === notificationId ? { ...n, read: true } : n
      ));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const allNotifications: Notification[] = [];
      const readNotifications = getReadNotifications();

      if (userRole === 'client' && companyId) {
        // First get project IDs
        const { data: companyProjects } = await supabase
          .from('projects')
          .select('id')
          .eq('company_id', companyId);

        const projectIds = (companyProjects as any)?.map((p: any) => p.id) || [];

        if (projectIds.length > 0) {
          // Fetch pending fund requests
          const { data: fundRequests } = await supabase
            .from('fund_requests')
            .select('id, project_id, ngo_id, amount, created_at, projects:project_id(name), ngos:ngo_id(legal_name)')
            .in('project_id', projectIds)
            .eq('status', 'pending')
            .order('created_at', { ascending: false })
            .limit(10);

          if (fundRequests) {
            fundRequests.forEach((req: any) => {
              allNotifications.push({
                id: `fund_${req.id}`,
                type: 'fund_request',
                title: 'New Fund Request',
                message: `${req.ngos?.legal_name || 'NGO'} requested ₹${req.amount?.toLocaleString('en-IN')} for ${req.projects?.name || 'project'}`,
                link: `/client/company/fund-requests/${req.id}/review`,
                read: readNotifications.has(`fund_${req.id}`),
                created_at: req.created_at
              });
            });
          }

          // Fetch projects pending review
          const { data: pendingProjects } = await supabase
            .from('project_ngos')
            .select('project_id, assigned_at, projects:project_id(id, name)')
            .in('project_id', projectIds)
            .eq('status', 'pending')
            .order('assigned_at', { ascending: false })
            .limit(10);

          if (pendingProjects) {
            pendingProjects.forEach((pn: any) => {
              allNotifications.push({
                id: `project_${pn.project_id}`,
                type: 'project_review',
                title: 'Project Pending Review',
                message: `Project "${pn.projects?.name || 'Project'}" is waiting for your review`,
                link: `/client/company/projects/${pn.project_id}/review`,
                read: readNotifications.has(`project_${pn.project_id}`),
                created_at: pn.assigned_at || new Date().toISOString()
              });
            });
          }
        }
      } else if (userRole === 'ngo' && ngoId) {
        // Fetch fund requests with clarification or rejection
        const { data: fundRequests } = await supabase
          .from('fund_requests')
          .select('id, project_id, status, clarification_request, rejected_reason, created_at, projects:project_id(name)')
          .eq('ngo_id', ngoId)
          .in('status', ['clarification_requested', 'rejected'])
          .order('created_at', { ascending: false })
          .limit(10);

        if (fundRequests) {
          fundRequests.forEach((req: any) => {
            if (req.status === 'clarification_requested' && req.clarification_request) {
              allNotifications.push({
                id: `clarify_${req.id}`,
                type: 'changes_requested',
                title: 'Clarification Requested',
                message: `Your fund request for "${req.projects?.name || 'project'}" needs clarification`,
                link: `/ngo/ngo/finance`,
                read: readNotifications.has(`clarify_${req.id}`),
                created_at: req.created_at
              });
            } else if (req.status === 'rejected' && req.rejected_reason) {
              allNotifications.push({
                id: `reject_${req.id}`,
                type: 'changes_requested',
                title: 'Fund Request Rejected',
                message: `Your fund request for "${req.projects?.name || 'project'}" was rejected`,
                link: `/ngo/ngo/finance`,
                read: readNotifications.has(`reject_${req.id}`),
                created_at: req.created_at
              });
            }
          });
        }

        // Fetch projects with changes requested
        const { data: projectsWithChanges, error: changesError } = await supabase
          .from('project_ngos')
          .select('project_id, requested_changes, assigned_at, projects:project_id(id, name, changes_requested)')
          .eq('ngo_id', ngoId)
          .eq('status', 'changes_requested')
          .order('assigned_at', { ascending: false })
          .limit(10);

        if (changesError) {
          console.error('Error fetching change requests:', changesError);
        }

        if (projectsWithChanges && projectsWithChanges.length > 0) {
          console.log('Found change requests:', projectsWithChanges);
          projectsWithChanges.forEach((pn: any) => {
            // Get change request text from either requested_changes or projects.changes_requested
            const changeRequestText = pn.requested_changes ||
              (pn.projects?.changes_requested
                ? (typeof pn.projects.changes_requested === 'object'
                  ? pn.projects.changes_requested.comment
                  : pn.projects.changes_requested)
                : '');

            // Truncate long messages for notification
            const shortMessage = changeRequestText && changeRequestText.length > 100
              ? changeRequestText.substring(0, 100) + '...'
              : changeRequestText || '';

            const notificationId = `changes_${pn.project_id}`;
            const isRead = readNotifications.has(notificationId);

            console.log('Adding change request notification:', {
              id: notificationId,
              projectId: pn.project_id,
              projectName: pn.projects?.name,
              isRead,
              changeRequestText
            });

            allNotifications.push({
              id: notificationId,
              type: 'changes_requested',
              title: 'Changes Requested',
              message: `Company requested changes to "${pn.projects?.name || 'project'}"${shortMessage ? `: ${shortMessage}` : ''}`,
              link: `/ngo/ngo/projects/${pn.project_id}/details`,
              read: isRead,
              created_at: pn.assigned_at || new Date().toISOString()
            });
          });
        }

        // Fetch active projects assigned to this NGO (notifications for when project becomes active)
        const { data: ngoProjects } = await supabase
          .from('project_ngos')
          .select('project_id, projects:project_id(id, name, status, updated_at)')
          .eq('ngo_id', ngoId);

        if (ngoProjects) {
          ngoProjects.forEach((pn: any) => {
            const proj = pn.projects;
            if (proj && proj.status === 'active') {
              // Only show notification if project was recently activated (within last 7 days)
              const updatedDate = new Date(proj.updated_at || proj.created_at);
              const daysSinceUpdate = (Date.now() - updatedDate.getTime()) / (1000 * 60 * 60 * 24);
              if (daysSinceUpdate <= 7) {
                allNotifications.push({
                  id: `active_${proj.id}`,
                  type: 'project_review',
                  title: 'Project Activated',
                  message: `Project "${proj.name || 'Project'}" is now active and ready to begin`,
                  link: `/ngo/ngo/projects/${proj.id}/details`,
                  read: readNotifications.has(`active_${proj.id}`),
                  created_at: proj.updated_at || new Date().toISOString()
                });
              }
            }
          });
        }
      }

      // Sort by created_at descending
      allNotifications.sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      setNotifications(allNotifications);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'fund_request': return '💰';
      case 'project_review': return '📋';
      case 'changes_requested': return '⚠️';
      case 'kpi_report': return '📊';
      case 'compliance': return '📄';
      default: return '🔔';
    }
  };

  return (
    <div className="notification-container" ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: '0.5rem',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          fontSize: '1.25rem'
        }}
      >
        🔔
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: '0',
              right: '0',
              background: '#ef4444',
              color: 'white',
              borderRadius: '50%',
              width: '18px',
              height: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              fontWeight: '600',
              border: '2px solid white'
            }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          className="notification-dropdown"
          style={{
            position: 'absolute',
            top: '100%',
            marginTop: '0.5rem',
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            maxHeight: '500px',
            overflowY: 'auto',
            zIndex: 1000,
            border: '1px solid var(--border)'
          }}
        >
          <div style={{ padding: '1rem', borderBottom: '1px solid var(--border)' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>Notifications</h3>
          </div>

          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <div className="spinner"></div>
            </div>
          ) : notifications.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--muted)' }}>
              No notifications
            </div>
          ) : (
            <div>
              {notifications.map((notification) => (
                <Link
                  key={notification.id}
                  to={notification.link}
                  onClick={() => {
                    if (!notification.read) {
                      markNotificationAsRead(notification.id);
                    }
                    setIsOpen(false);
                  }}
                  style={{
                    display: 'block',
                    padding: '1rem',
                    borderBottom: '1px solid var(--border-light)',
                    textDecoration: 'none',
                    color: 'inherit',
                    transition: 'background 0.2s',
                    background: notification.read ? 'transparent' : '#f0f9ff'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f9fafb';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = notification.read ? 'transparent' : '#f0f9ff';
                  }}
                >
                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'start' }}>
                    <span style={{ fontSize: '1.5rem' }}>{getNotificationIcon(notification.type)}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', marginBottom: '0.25rem', fontSize: '0.875rem' }}>
                        {notification.title}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>
                        {notification.message}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--muted)' }}>
                        {new Date(notification.created_at).toLocaleString()}
                      </div>
                    </div>
                    {!notification.read && (
                      <span
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: '#3b82f6',
                          flexShrink: 0,
                          marginTop: '0.25rem'
                        }}
                      />
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
